// ── Member Assignee combo-box (C1252) ──
// Typeahead combo-box for the "Member Assignee" field: filterable text input over the
// project member list, replaces plain-<select>/avatar-popup assignee controls with a
// keyboard-navigable, always-unassignable field. Port of the Task App's
// _createMemberCombobox (ai/todo/server/src/client/task-board.js:2753-2919) — same
// behavior, adapted to the web frontend's plain-JS conventions (no global `state`, no
// i18n — this app has none, so strings are hardcoded English).

import { escapeHtml, renderAvatarHtml } from '../utils.js';

// escapeHtml() escapes & < > via textContent->innerHTML but NOT '"'. The committed
// member name is written into a `value="…"` attribute below, so it needs the extra pass.
function escapeAttr(s) {
  return escapeHtml(s).replace(/"/g, '&quot;');
}

// Pending invites (user_id/name null) never resolve to a real assignee — same filter
// assignee-picker.js now uses (C1252 bugfix, see removeTag/renderOptions there).
function acceptedMembers(members) {
  return (members || []).filter(m => m.user_id != null && m.name != null);
}

// Resolves an assignee id to its display label. Falls back to `#<id>` when the id
// doesn't match any loaded member (member removed, members not yet fetched) — a blank
// field would read as "unassigned", which is wrong; the id is at least honest.
function memberLabelFor(id, members) {
  if (id == null) return '';
  const m = acceptedMembers(members).find(m => Number(m.user_id) === Number(id));
  return m ? m.name : `#${id}`;
}

// buildMemberOptions(members, query) — the dropdown option list for a given filter query.
// Exported (also used by createMemberCombo()'s buildOptions() below) so it's unit-testable
// without spinning up the whole combo instance. Every match is mapped to `{ id: m.user_id,
// … }` — `id` is what commit() below writes straight into task.assignee, and it MUST be
// users.id, never the project_members row id (`m.id`, literally 0 for the project owner).
// C1525: the raw member row was previously spread in unmapped, so picking a member wrote
// the membership-row id into task.assignee, which then rendered as `#<id>` (memberLabelFor
// resolves strictly on user_id) and blanked the sibling avatar. `member` keeps the raw row
// alongside so renderAvatarHtml() (called from renderDropdown() below) still sees its
// original input — avatar_url and the `user.id || user.user_id` color-hash key — byte-
// identical to every other avatar in the SPA; overwriting `id` in place (the Task App
// original's `_normalizeProjectMember()` approach) would instead recolor non-owner members'
// avatars here relative to the sibling `.task-detail-assignee-avatar`/board-card avatars,
// which are rendered from the unmodified row.
export function buildMemberOptions(members, query) {
  const q = (query || '').trim().toLowerCase();
  const none = { id: null, name: 'Unassigned' };
  const matches = acceptedMembers(members)
    .filter(m => (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q))
    .map(m => ({ id: m.user_id, name: m.name, email: m.email, member: m }));
  // (none) is always offered, never filtered — otherwise there is no keyboard path
  // to unassign once any text has been typed.
  return [none, ...matches];
}

export function renderMemberCombo({ id, assignee, members, disabled = false }) {
  return `<div class="member-combo" data-id="${escapeAttr(id)}">
    <input class="member-combo-input form-input form-input-sm" type="text" role="combobox" aria-autocomplete="list" aria-expanded="false" aria-controls="${escapeAttr(id)}-options" autocomplete="off" placeholder="Search members..." value="${escapeAttr(memberLabelFor(assignee, members))}"${disabled ? ' disabled' : ''}>
    <input type="hidden" class="member-combo-value" value="${assignee ?? ''}">
  </div>`;
}

// Host-agnostic behavior factory. Options:
//   getMembers()         -> member[]        live list (host state can change)
//   getValue()           -> number|null     current assignee id
//   setValue(id|null)    -> void            commit id into the host's state
//   onCommit(id|null)    -> void            called after a value is committed (PATCH, etc.)
//   isAlive()            -> boolean         false once the host UI is gone
//   scrollEl             -> Element|null    scroll container to reposition against
//   isDisabled()         -> boolean         optional lock predicate (default: false)
// Returns { destroy, refresh, setDisabled, close }.
export function createMemberCombo(root, opts) {
  const { getMembers, getValue, setValue, onCommit, isAlive, scrollEl } = opts;
  const isDisabled = opts.isDisabled || (() => false);
  const combo = root.querySelector('.member-combo');
  const input = combo?.querySelector('.member-combo-input');
  const valueInput = combo?.querySelector('.member-combo-value');
  if (!combo || !input || !valueInput) return { destroy() {}, refresh() {}, setDisabled() {}, close() {} };

  let dropdown = null;
  let options = [];
  let activeIndex = -1;
  let blurTimer = null;

  function syncLabel() {
    input.value = memberLabelFor(getValue(), getMembers());
  }

  function positionDropdown() {
    if (!dropdown) return;
    const r = combo.getBoundingClientRect();
    dropdown.style.top = `${input.getBoundingClientRect().bottom + 2}px`;
    dropdown.style.left = `${r.left}px`;
    dropdown.style.width = `${Math.max(r.width, 180)}px`;
  }

  const onScrollOrResize = () => positionDropdown();
  if (scrollEl) scrollEl.addEventListener('scroll', onScrollOrResize, true);
  window.addEventListener('resize', onScrollOrResize);

  function buildOptions(query) { return buildMemberOptions(getMembers(), query); }

  function updateDropdownActive() {
    if (!dropdown) return;
    dropdown.querySelectorAll('.member-combo-option').forEach((el, i) => {
      el.classList.toggle('active', i === activeIndex);
    });
  }

  function onDropdownMousedown(e) {
    const opt = e.target.closest('.member-combo-option');
    if (!opt) return;
    e.preventDefault(); // keep focus on the input — don't let the click blur it first
    const entry = options[Number(opt.dataset.index)];
    if (entry) commit(entry);
  }

  function renderDropdown() {
    if (!dropdown) {
      dropdown = document.createElement('div');
      dropdown.className = 'member-combo-dropdown';
      dropdown.id = `${combo.dataset.id}-options`;
      dropdown.addEventListener('mousedown', onDropdownMousedown);
      document.body.appendChild(dropdown);
    }
    options = buildOptions(input.value);
    activeIndex = options.length ? 0 : -1;
    dropdown.innerHTML = options.length
      ? options.map((m, i) => `<div class="member-combo-option" data-index="${i}">${m.id == null ? '' : renderAvatarHtml(m.member || m, 20)}<span>${escapeHtml(m.name)}</span></div>`).join('')
      : `<div class="member-combo-empty">No members</div>`;
    updateDropdownActive();
    positionDropdown();
  }

  function hideDropdown() {
    if (dropdown) { dropdown.remove(); dropdown = null; }
    options = [];
    activeIndex = -1;
    input.setAttribute('aria-expanded', 'false');
  }

  function openDropdown() {
    if (isDisabled()) return;
    input.setAttribute('aria-expanded', 'true');
    renderDropdown();
  }

  function commit(entry) {
    setValue(entry.id);
    valueInput.value = entry.id ?? '';
    syncLabel();
    hideDropdown();
    if (onCommit) onCommit(entry.id);
  }

  input.addEventListener('focus', () => {
    if (blurTimer) { clearTimeout(blurTimer); blurTimer = null; }
    input.select();
    openDropdown();
  });

  input.addEventListener('click', () => { if (!dropdown) openDropdown(); });

  input.addEventListener('input', () => { renderDropdown(); });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!dropdown) { openDropdown(); return; }
      if (options.length) { activeIndex = (activeIndex + 1) % options.length; updateDropdownActive(); }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (dropdown && options.length) { activeIndex = (activeIndex - 1 + options.length) % options.length; updateDropdownActive(); }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (dropdown && options.length && activeIndex >= 0) commit(options[activeIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (dropdown) {
        // Dropdown open: close it and stop the keypress from reaching a host-level
        // Escape handler (e.g. openTaskDetail()'s document listener), which would
        // otherwise close the whole panel/modal instead of just the dropdown.
        e.stopPropagation();
        syncLabel();
        hideDropdown();
      } else {
        e.stopPropagation();
        syncLabel();
        input.blur();
      }
    }
  });

  input.addEventListener('blur', () => {
    blurTimer = setTimeout(() => {
      if (!isAlive()) return;
      hideDropdown();
      syncLabel();
    }, 150);
  });

  function refresh() {
    setDisabled(isDisabled());
    if (document.activeElement !== input) syncLabel();
    else if (dropdown) renderDropdown();
  }

  function setDisabled(v) {
    input.disabled = !!v;
    if (v) hideDropdown();
  }

  function destroy() {
    hideDropdown();
    if (blurTimer) { clearTimeout(blurTimer); blurTimer = null; }
    if (scrollEl) scrollEl.removeEventListener('scroll', onScrollOrResize, true);
    window.removeEventListener('resize', onScrollOrResize);
  }

  syncLabel();
  setDisabled(isDisabled());

  return { destroy, refresh, setDisabled, close: hideDropdown };
}
